import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell, BellOff, Check, CheckCheck, Trash2, RefreshCw,
  UserPlus, MessageCircle, AtSign, Trophy, Flag, AlertTriangle,
  Mail, XCircle, CheckCircle, User
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const relativeTime = (ts) => {
  if (!ts) return 'Just now';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
};

const getIconMeta = (type) => {
  const t = String(type || '').toLowerCase();
  if (t.includes('message_received')) return { Icon: Mail, color: '#BAE6FD', bg: 'rgba(165,180,252,0.12)' };
  if (t.includes('follow_accepted')) return { Icon: CheckCircle, color: '#22C55E', bg: 'rgba(34,197,94,0.12)' };
  if (t.includes('new_follower') || t.includes('follow_request')) return { Icon: UserPlus, color: '#38BDF8', bg: 'rgba(56,189,248,0.12)' };
  if (t.includes('mention')) return { Icon: AtSign, color: '#FBBF24', bg: 'rgba(251,191,36,0.12)' };
  if (t.includes('comment')) return { Icon: MessageCircle, color: '#38BDF8', bg: 'rgba(56,189,248,0.12)' };
  if (t.includes('started')) return { Icon: Flag, color: '#F97316', bg: 'rgba(249,115,22,0.12)' };
  if (t.includes('winner') || t.includes('trophy')) return { Icon: Trophy, color: '#FBBF24', bg: 'rgba(251,191,36,0.12)' };
  if (t.includes('rejected')) return { Icon: XCircle, color: '#EF4444', bg: 'rgba(239,68,68,0.12)' };
  if (t.includes('warning')) return { Icon: AlertTriangle, color: '#F97316', bg: 'rgba(249,115,22,0.12)' };
  return { Icon: Bell, color: '#38BDF8', bg: 'rgba(56,189,248,0.12)' };
};

function NotifItem({ notif, onMarkRead, onDelete, onAction }) {
  const { Icon, color, bg } = getIconMeta(notif.notification_type || notif.type);
  const isUnread = !notif.is_read && !notif.read;
  const isFollowRequest = String(notif.notification_type || notif.type || '').toLowerCase().includes('follow_request');

  const title = notif.title || notif.headline || '';
  const body = notif.message || notif.body || notif.content || notif.text || '';
  const actor = notif.actor_username || notif.sender_username || notif.from_user?.username || notif.from?.username || '';
  const actorPhoto = notif.actor_photo || notif.sender_photo || notif.from_user?.profile_picture || '';
  const ts = notif.created_at || notif.timestamp || notif.date;
  const followId = notif.follow_id || notif.object_id || notif.id;

  return (
    <div className={`notif-item ${isUnread ? 'notif-unread' : ''}`}
      onClick={() => isUnread && onMarkRead(notif.id)}>
      <div className="notif-icon-wrap" style={{ background: bg }}>
        <Icon size={18} color={color} />
      </div>

      <div className="notif-body">
        <div className="notif-top-row">
          {actorPhoto && (
            <img src={actorPhoto} alt={actor} className="notif-actor-avatar"
              onError={(e) => e.target.style.display = 'none'} />
          )}
          <div className="notif-text-wrap">
            {(title || actor) && (
              <div className="notif-title">
                {actor ? <strong>{actor}</strong> : null}
                {title && <span> {title}</span>}
              </div>
            )}
            {body && <div className="notif-body-text">{body}</div>}
          </div>
          {isUnread && <span className="notif-unread-dot" />}
        </div>

        <div className="notif-footer-row">
          <span className="notif-time">{relativeTime(ts)}</span>

          {isFollowRequest && !notif._actionDone && (
            <div className="notif-actions">
              <button className="notif-action-btn accept"
                onClick={(e) => { e.stopPropagation(); onAction(followId, 'accept', notif.id); }}>
                <Check size={14} /> Accept
              </button>
              <button className="notif-action-btn reject"
                onClick={(e) => { e.stopPropagation(); onAction(followId, 'reject', notif.id); }}>
                <XCircle size={14} /> Decline
              </button>
            </div>
          )}
          {notif._actionDone && (
            <span className="notif-action-done">{notif._actionDone}</span>
          )}
        </div>
      </div>

      <button className="notif-delete-btn" onClick={(e) => { e.stopPropagation(); onDelete(notif.id); }}
        title="Delete">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export default function NotificationsPage() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  const normalize = (payload) => {
    let arr;
    if (Array.isArray(payload)) arr = payload;
    else if (Array.isArray(payload?.results)) arr = payload.results;
    else if (Array.isArray(payload?.notifications)) arr = payload.notifications;
    else arr = [];
    return arr.map((n, idx) => ({ ...n, id: n.id ?? `notif-${idx}` }));
  };

  const fetchNotifications = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    setError('');
    try {
      const data = await api.getNotifications(token);
      setNotifications(normalize(data));
    } catch {
      setError('Could not load notifications.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    try { await api.markNotificationRead(token, id); } catch { }
  };

  const handleMarkAllRead = async () => {
    setSubmitting(true);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    try { await api.markAllRead(token); } catch { }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    try { await api.deleteNotification(token, id); } catch { }
  };

  const handleFollowAction = async (followId, action, notifId) => {
    try {
      if (action === 'accept') {
        await api.acceptFollowRequest(token, followId);
        setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true, _actionDone: 'Accepted ✓' } : n));
      } else {
        await api.rejectFollowRequest(token, followId);
        setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true, _actionDone: 'Declined' } : n));
      }
    } catch {
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, _actionDone: 'Failed' } : n));
    }
  };

  const displayed = notifications.filter(n => {
    if (filter === 'unread') return !n.is_read && !n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.is_read && !n.read).length;

  return (
    <div className="notif-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <Bell size={24} className="page-title-icon" />
            Notifications
          </h1>
          {unreadCount > 0 && <span className="unread-count-badge">{unreadCount} new</span>}
        </div>
        <div className="notif-actions-top">
          {unreadCount > 0 && (
            <button className="icon-btn-labeled" onClick={handleMarkAllRead} disabled={submitting}>
              <CheckCheck size={16} />
              <span>Mark all read</span>
            </button>
          )}
          <button className="icon-btn" onClick={() => fetchNotifications()} title="Refresh">
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="notif-filter-row">
        <button className={`filter-chip ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}>All</button>
        <button className={`filter-chip ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}>
          Unread {unreadCount > 0 && <span className="chip-badge">{unreadCount}</span>}
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="notif-skeleton">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="notif-skeleton-item">
              <div className="sk-circle small" />
              <div className="sk-lines">
                <div className="sk-line wide" />
                <div className="sk-line narrow" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="feed-error">
          <p>{error}</p>
          <button className="auth-btn" onClick={() => fetchNotifications()} style={{ width: 'auto', padding: '10px 24px' }}>Retry</button>
        </div>
      ) : displayed.length === 0 ? (
        <div className="feed-empty">
          <BellOff size={48} color="rgba(14,165,233,0.4)" />
          <p>{filter === 'unread' ? 'All caught up!' : 'No notifications yet'}</p>
          <span>{filter === 'unread' ? 'You have no unread notifications.' : 'Activity will appear here.'}</span>
        </div>
      ) : (
        <div className="notif-list">
          {displayed.map(n => (
            <NotifItem key={n.id} notif={n}
              onMarkRead={handleMarkRead}
              onDelete={handleDelete}
              onAction={handleFollowAction} />
          ))}
        </div>
      )}
    </div>
  );
}
